import { useEffect, useRef } from 'react'
import { Bell, Check, CheckCheck, Trash2, X, DollarSign } from 'lucide-react'
import { useNotificationStore } from '../../stores/notificationStore'
import type { AppNotification } from '../../types'

function timeAgo(dateStr: string): string {
  const now = Date.now()
  const then = new Date(dateStr + 'Z').getTime()
  const diff = Math.max(0, now - then)
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 7) return `${days}d ago`
  return new Date(dateStr).toLocaleDateString()
}

function formatAmount(amount: number | undefined, currency?: string): string {
  if (amount === undefined || amount === null) return ''
  const abs = Math.abs(amount)
  const formatted = abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return currency && currency !== 'USD' ? `${formatted} ${currency}` : `$${formatted}`
}

function NotificationItem({ notification, onMarkRead, onDelete }: {
  notification: AppNotification
  onMarkRead: (id: string) => void
  onDelete: (id: string) => void
}) {
  const isUnread = notification.status === 'unread'
  const amount = notification.metadata?.amount
  const currency = notification.metadata?.currency

  return (
    <div
      className={`group flex gap-3 px-4 py-3 border-b border-slate-800 transition-colors ${
        isUnread ? 'bg-indigo-950/30' : 'bg-transparent hover:bg-slate-800/50'
      }`}
    >
      {/* Icon */}
      <div className={`mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${
        isUnread ? 'bg-indigo-600/20 text-indigo-400' : 'bg-slate-800 text-slate-500'
      }`}>
        <DollarSign className="h-4 w-4" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className={`text-sm leading-snug ${isUnread ? 'text-white font-medium' : 'text-slate-300'}`}>
            {notification.body}
          </p>
          {isUnread && (
            <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-indigo-500" />
          )}
        </div>
        <div className="mt-1 flex items-center gap-2 text-xs text-slate-500">
          <span>{timeAgo(notification.created_at)}</span>
          {amount !== undefined && (
            <>
              <span className="text-slate-700">·</span>
              <span className="font-medium text-slate-400">{formatAmount(amount, currency)}</span>
            </>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-shrink-0 items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {isUnread && (
          <button
            onClick={() => onMarkRead(notification.id)}
            className="rounded p-1 text-slate-500 hover:bg-slate-700 hover:text-slate-300"
            title="Mark as read"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
        )}
        <button
          onClick={() => onDelete(notification.id)}
          className="rounded p-1 text-slate-500 hover:bg-slate-700 hover:text-red-400"
          title="Delete"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}

export function NotificationPanel() {
  const panelRef = useRef<HTMLDivElement>(null)
  const {
    notifications, unreadCount, loading, panelOpen,
    closePanel, markRead, markAllRead, deleteNotification,
  } = useNotificationStore()

  // Close on click outside
  useEffect(() => {
    if (!panelOpen) return
    function handleClick(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        // Check if clicked on the bell button (has data-notification-bell)
        const target = e.target as HTMLElement
        if (target.closest('[data-notification-bell]')) return
        closePanel()
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [panelOpen, closePanel])

  if (!panelOpen) return null

  return (
    <div
      ref={panelRef}
      className="absolute right-0 top-full mt-2 w-96 max-h-[32rem] flex flex-col rounded-xl border border-slate-700 bg-slate-900 shadow-2xl z-50 overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-800 px-4 py-3">
        <div className="flex items-center gap-2">
          <Bell className="h-4 w-4 text-slate-400" />
          <h3 className="text-sm font-semibold text-white">Notifications</h3>
          {unreadCount > 0 && (
            <span className="rounded-full bg-indigo-600/20 px-2 py-0.5 text-xs font-medium text-indigo-400">
              {unreadCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {unreadCount > 0 && (
            <button
              onClick={markAllRead}
              className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-slate-400 hover:bg-slate-800 hover:text-slate-200 transition-colors"
              title="Mark all as read"
            >
              <CheckCheck className="h-3.5 w-3.5" />
              <span>Read all</span>
            </button>
          )}
          <button
            onClick={closePanel}
            className="rounded-lg p-1 text-slate-500 hover:bg-slate-800 hover:text-slate-300 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {loading && notifications.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-sm text-slate-500">
            Loading...
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-slate-500">
            <Bell className="mb-2 h-8 w-8 text-slate-700" />
            <p className="text-sm">No notifications yet</p>
            <p className="mt-1 text-xs text-slate-600">
              You&apos;ll be notified when transactions post
            </p>
          </div>
        ) : (
          notifications.map((n) => (
            <NotificationItem
              key={n.id}
              notification={n}
              onMarkRead={markRead}
              onDelete={deleteNotification}
            />
          ))
        )}
      </div>
    </div>
  )
}
